import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Collapse,
  IconButton,
  Divider,
  Chip,
  Tooltip,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ApproveSolistapi,
  ProjectSummaryStats,
  ProjectSummaryRpt,
  ProjectSummaryExport,
} from "../../../Services/Services";
import { Card, CardContent, Grid } from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { Description as FileIcon } from "@mui/icons-material";
import ProSummaryPi from "../Reports/ProjectSummary/ProSummaryPi";
import Solist from "../Reports/ProjectSummary/Solist";
import GeneralVoucher from "../Reports/ProjectSummary/GeneralVoucher";
import PurchaseVoucher from "../Reports/ProjectSummary/PurchaseVoucher";
import ReceiptVoucher from "../Reports/ProjectSummary/ReceiptVoucher";
import SaleVoucher from "../Reports/ProjectSummary/SalesVoucher";
import PaymentVoucher from "../Reports/ProjectSummary/PaymentVoucher";
import PurchaseOrderSummaryList from "../Reports/ProjectSummary/PurchaseOrderSummaryList";
import Material_inward_register from "../Reports/ProjectSummary/IssueNoteInward";
import Material_issue_register from "../Reports/ProjectSummary/IssueNoteOutward";
import Expense from "./ProjectSummary/Expense";
import debounce from "lodash.debounce";

const CARD_COLORS = {
  SO: {
    bg: "linear-gradient(135deg,#F1F8E9 0%,#DCEDC8 100%)",
    border: "#7CB342",
    text: "#33691E",
  },
  PI: {
    bg: "linear-gradient(135deg,#E3F2FD 0%,#BBDEFB 100%)",
    border: "#2196f3",
    text: "#0D47A1",
  },
  Journal: {
    bg: "linear-gradient(135deg,#F3E5F5 0%,#E1BEE7 100%)",
    border: "#9C27B0",
    text: "#4A148C",
  },
  PurchaseVoucher: {
    bg: "linear-gradient(135deg,#FFF3E0 0%,#FFE0B2 100%)",
    border: "#FB8C00",
    text: "#E65100",
  },
  SalesVoucher: {
    bg: "linear-gradient(135deg,#EDE7F6 0%,#C5CAE9 100%)",
    border: "#3F51B5",
    text: "#1A237E",
  },
  // ✅ NEW: PO (Purchase Orders)
  PO: {
    bg: "linear-gradient(135deg,#FFEBEE 0%,#FFCDD2 100%)",
    border: "#E53935",
    text: "#B71C1C",
  },
  Payment: {
    bg: "linear-gradient(135deg,#FFEBEE 0%,#FFCDD2 100%)",
    border: "#E53935",
    text: "#B71C1C",
  },
  Receipt: {
    bg: "linear-gradient(135deg,#E8F5E9 0%,#C8E6C9 100%)",
    border: "#43A047",
    text: "#1B5E20",
  },
  IssueNoteInward: {
    bg: "linear-gradient(135deg,#E0F7FA 0%,#B2EBF2 100%)",
    border: "#00ACC1",
    text: "#006064",
  },
  IssueNoteOutward: {
    bg: "linear-gradient(135deg,#E0F7FA 0%,#B2EBF2 100%)",
    border: "#00ACC1",
    text: "#006064",
  },
  Expense: {
    bg: "linear-gradient(135deg,#E0F7FC 0%,#B2EBF2 100%)",
    border: "#00ACC1",
    text: "#006064",
  },
};

const ProjectSummary = () => {
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectAmt, setprojectAmt] = useState({});
  const [tabData, setTabData] = useState([]);
  const [selectedTab, setSelectedTab] = useState();
  const [expandedRow, setExpandedRow] = useState(null);
  const [projectSearch, setProjectSearch] = useState("");
  const [projectPage, setProjectPage] = useState(1);
  const [projectHasMore, setProjectHasMore] = useState(true);

  const [excelLoading, setExcelLoading] = useState(false);
  const [cardExcelLoading, setCardExcelLoading] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  const fetchReportData = async (key) => {
    if (!selectedProject) return;
    try {
      const res = await ProjectSummaryRpt(selectedProject.id, { key });
      setTabData(res || []);
      setExpandedRow(null);
    } catch (error) {
      console.error("Failed to fetch report:", error);
      setTabData([]);
    }
  };

  const fetchProjects = async (page = 1, search = "") => {
    setLoadingProjects(true);
    try {
      const res = await ApproveSolistapi(page, { search });
      if (page === 1) {
        setProjects(res?.results || res || []);
      } else {
        setProjects((prev) => [...prev, ...(res?.results || res || [])]);
      }
      setProjectHasMore(!!res?.next);
    } catch (e) {
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleProjectSearch = debounce((value) => {
    setProjectSearch(value);
    setProjectPage(1);
    fetchProjects(1, value);
  }, 500);

  useEffect(() => {
    fetchProjects(1, "");
  }, []);

  useEffect(() => {
    const loadSummary = async () => {
      if (!selectedProject) return;
      try {
        const res = await ProjectSummaryStats(selectedProject.id);
        setprojectAmt(res?.summary || {});
      } catch (e) {
        setprojectAmt({});
      }
    };
    loadSummary();
  }, [selectedProject]);

  // ══════════════════════════════════════════════════
  // Excel Download Helper
  // ══════════════════════════════════════════════════
  const downloadExcel = async (key, filename) => {
    const blob = await ProjectSummaryExport(selectedProject.id, key);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleAllExcelExport = async () => {
    if (!selectedProject) {
      toast.warning("Please select a project first!");
      return;
    }
    setExcelLoading(true);
    try {
      const projectName = (selectedProject.project_name || selectedProject.so_no || selectedProject.id)
        .toString()
        .replace(/[^a-zA-Z0-9_-]/g, "_");
      const dateStr = new Date().toISOString().split("T")[0];
      await downloadExcel("ALL", `Project_Summary_${projectName}_${dateStr}.xlsx`);
      toast.success("All data exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setExcelLoading(false);
    }
  };

  const handleCardExcelExport = async (e, key, label) => {
    e.stopPropagation();
    if (!selectedProject) {
      toast.warning("Please select a project first!");
      return;
    }
    setCardExcelLoading(key);
    try {
      const projectName = (selectedProject.project_name || selectedProject.so_no || selectedProject.id)
        .toString()
        .replace(/[^a-zA-Z0-9_-]/g, "_");
      const dateStr = new Date().toISOString().split("T")[0];
      await downloadExcel(key, `${label}_${projectName}_${dateStr}.xlsx`);
      toast.success(`${label} exported successfully!`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setCardExcelLoading(null);
    }
  };

  const renderSection = () => {
    if (!selectedProject) return null;
    switch (selectedTab) {
      case "SO": return <Solist data={tabData} />;
      case "PI": return <ProSummaryPi data={tabData} />;
      case "Journal": return <GeneralVoucher data={tabData} />;
      case "PurchaseVoucher": return <PurchaseVoucher data={tabData} />;
      case "SalesVoucher": return <SaleVoucher data={tabData} />;
case "PO": return <PurchaseOrderSummaryList data={tabData} />;
      case "Payment": return <PaymentVoucher data={tabData} />;
      case "Receipt": return <ReceiptVoucher data={tabData} />;
      case "IssueNoteInward": return <Material_inward_register data={tabData} />;
      case "IssueNoteOutward": return <Material_issue_register data={tabData} />;
      case "Expense": return <Expense data={tabData} />;
      default: return null;
    }
  };

  const renderMobileData = () => {
  if (!selectedTab || !tabData) return null;
  const dataArray = Array.isArray(tabData)
    ? tabData
    : tabData?.results || tabData?.data || [];

  if (dataArray.length === 0) {
    return (
      <Box sx={{ p: 3, mt: 10, textAlign: "center", bgcolor: "#f5f5f5", borderRadius: 1 }}>
        <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
          No data available
        </Typography>
      </Box>
    );
  }

  // ✅ Define fields per tab (matches desktop table columns)
  const getTabFields = (row, tab) => {
    switch (tab) {
      case "SO":
        return {
          identifier: row.so_no,
          partyName: row.party_details?.company_name || "N/A",
          amount: row.grand_total,
          status: row.status,
          fields: [
            { label: "PROJECT", value: row.project_name },
            { label: "GRAND TOTAL", value: row.grand_total ? `₹${Number(row.grand_total).toLocaleString("en-IN")}` : "₹0" },
            { label: "AD REC", value: row.rec_amount ? `₹${Number(row.rec_amount).toLocaleString("en-IN")}` : "₹0" },
            { label: "BAL AMT", value: `₹${(Number(row.grand_total || 0) - Number(row.rec_amount || 0)).toFixed(1)}` },
            { label: "PO NO", value: row.po_number },
            { label: "PO DATE", value: row.order_date ? new Date(row.order_date).toLocaleDateString("en-GB") : "N/A" },
            { label: "DEBTOR", value: row.party_details?.company_name },
            { label: "DATE", value: row.created_at ? new Date(row.created_at).toLocaleDateString("en-GB") : "N/A" },
          ],
        };

      case "PI":
        return {
          identifier: row.pi_no || row.pi_number,
          partyName: row.project?.party_name || row.party_name || "N/A",
          amount: row.basic_value || row.net_payable,
          status: row.status,
          fields: [
            { label: "PI DATE", value: row.pi_date ? new Date(row.pi_date).toLocaleDateString("en-GB") : "N/A" },
            { label: "RA BILL NO", value: row.ra_bill_number },
            { label: "PROJECT", value: row.project?.project_name },
            { label: "SO NO", value: row.project?.so_no },
            { label: "BASIC VALUE", value: row.basic_value ? `₹${Number(row.basic_value).toLocaleString("en-IN")}` : "₹0" },
            { label: "GST AMOUNT", value: row.gst_amount ? `₹${Number(row.gst_amount).toLocaleString("en-IN")}` : "₹0" },
            { label: "ADVANCE RECEIVED", value: row.advance_received ? `₹${Number(row.advance_received).toLocaleString("en-IN")}` : "₹0" },
            { label: "NET PAYABLE", value: row.net_payable ? `₹${Number(row.net_payable).toLocaleString("en-IN")}` : "₹0" },
          ],
        };

      case "PurchaseVoucher":
        return {
          identifier: row.purchase_number,
          partyName: row.supplier_name || "N/A",
          amount: row.total_amount,
          status: row.status,
          fields: [
            { label: "DATE", value: row.created_at ? new Date(row.created_at).toLocaleDateString("en-GB") : "N/A" },
            { label: "PARTY NAME", value: row.supplier_name },
            { label: "TOTAL AMOUNT", value: row.total_amount ? `₹${Number(row.total_amount).toLocaleString("en-IN")}` : "₹0" },
            { label: "RECEIPT AMOUNT", value: row.receipt_amount ? `₹${Number(row.receipt_amount).toLocaleString("en-IN")}` : "₹0" },
            { label: "BALANCE", value: row.balance_amount ? `₹${Number(row.balance_amount).toLocaleString("en-IN")}` : "₹0" },
          ],
        };

      case "PO":
        const items = row.items || [];
        const totalQty = items.reduce((sum, it) => sum + (parseFloat(it.quantity) || 0), 0);
        const totalAmt = items.reduce((sum, it) => sum + (parseFloat(it.amount) || 0), 0);
        return {
          identifier: row.po_number,
          partyName: row.supplier_name || row.billing_name || "N/A",
          amount: totalAmt,
          status: row.approval_status,
          fields: [
            { label: "DATE", value: row.created_at ? new Date(row.created_at).toLocaleDateString("en-GB") : "N/A" },
            { label: "SUPPLIER", value: row.supplier_name },
            { label: "PROJECT", value: row.project_name },
            { label: "BILLING NAME", value: row.billing_name },
            { label: "BILLING GST", value: row.billing_gst },
            { label: "CONTACT", value: row.billing_contact },
            { label: "ITEMS", value: items.length },
            { label: "TOTAL QTY", value: totalQty },
            { label: "TOTAL AMOUNT", value: `₹${totalAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}` },
          ],
        };

      case "IssueNoteInward":
        const inwardItems = row.items || [];
        return {
          identifier: row.grn_no || row.purchase_number,
          partyName: row.supplier_name || "N/A",
          amount: row.total_amount,
          status: row.status,
          fields: [
            { label: "DATE", value: row.created_at ? new Date(row.created_at).toLocaleDateString("en-GB") : "N/A" },
            { label: "GRN NO", value: row.grn_no },
            { label: "PO NO", value: row.purchase_order },
            { label: "SUPPLIER", value: row.supplier_name },
            { label: "ITEMS COUNT", value: inwardItems.length },
          ],
        };

      case "IssueNoteOutward":
        const outwardItems = row.items || [];
        return {
          identifier: row.slip_no,
          partyName: row.project_name || "N/A",
          amount: 0,
          status: null,
          fields: [
            { label: "DATE", value: row.created_at ? new Date(row.created_at).toLocaleDateString("en-GB") : "N/A" },
            { label: "REQ SLIP NO", value: row.slip_no },
            { label: "PROJECT", value: row.project_name },
            { label: "ITEMS COUNT", value: outwardItems.length },
          ],
        };

      default:
        return {
          identifier: `#${row.id || ""}`,
          partyName: "N/A",
          amount: 0,
          status: null,
          fields: [],
        };
    }
  };

  return (
    <Box sx={{ bgcolor: "background.paper" }}>
      {dataArray.map((row, index) => {
        const { identifier, partyName, amount, status, fields } = getTabFields(row, selectedTab);

        return (
          <Box key={index}>
            {/* Main Row - Always Visible */}
            <Box
              onClick={() => setExpandedRow(expandedRow === index ? null : index)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                cursor: "pointer",
                bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                "&:active": { bgcolor: "#e3f2fd" },
                borderLeft: `4px solid ${CARD_COLORS[selectedTab]?.border || "#1976d2"}`,
                mt: index === 0 ? { xs: 4, sm: 0 } : 0,
              }}
            >
              <Box sx={{ flex: 1 }}>
                {/* Identifier + Status */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Typography sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}>
                    #{index + 1}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: "bold", flex: 1 }}>
                    {identifier || "N/A"}
                  </Typography>
                  {status && (
                    <Chip
                      label={status}
                      color={
                        status === "Approved"
                          ? "success"
                          : status === "Rejected"
                            ? "error"
                            : "warning"
                      }
                      size="small"
                      sx={{ fontSize: "0.6rem", height: 18 }}
                    />
                  )}
                </Box>

                {/* Party Name */}
                <Typography sx={{ fontSize: "0.72rem", color: "#666", mb: 0.3 }}>
                  {partyName}
                </Typography>

                {/* Amount */}
                {amount > 0 && (
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: "bold", color: "primary.main" }}>
                    ₹{Number(amount).toLocaleString("en-IN")}
                  </Typography>
                )}
              </Box>
              <IconButton size="small" sx={{ ml: 1 }}>
                {expandedRow === index ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Box>

            {/* Expanded Details */}
            <Collapse in={expandedRow === index} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                  borderLeft: `4px solid ${CARD_COLORS[selectedTab]?.border || "#1976d2"}`,
                }}
              >
                {/* ✅ 2 Column Grid - Only required fields */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 0.8,
                  }}
                >
                  {fields.map((field, i) => (
                    <Box key={i}>
                      <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                        {field.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#333",
                          wordBreak: "break-word",
                        }}
                      >
                        {field.value || "N/A"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Collapse>
            <Divider />
          </Box>
        );
      })}
    </Box>
  );
};
  const SummaryCard = ({ tabKey, label, onClick }) => (
    <Card
      onClick={onClick}
      sx={{
        width: "100%",
        minHeight: { xs: 80, sm: 50 },
        cursor: "pointer",
        p: { xs: 0.3, sm: 0.5 },
        background: CARD_COLORS[tabKey].bg,
        border: selectedTab === tabKey ? "2px solid #1976d2" : `1px solid ${CARD_COLORS[tabKey].border}`,
        color: CARD_COLORS[tabKey].text,
        borderRadius: 1.5,
        boxShadow: 1,
        transition: "transform .18s ease, box-shadow .18s ease",
        "&:hover": { transform: "translateY(-3px)", boxShadow: 3 },
        position: "relative",
      }}
    >
      <CardContent sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: 0.3, sm: 0.5 },
        p: { xs: "8px !important", sm: "12px !important" },
      }}>
        <Typography variant="subtitle2" sx={{
          fontWeight: 600,
          fontSize: { xs: "0.7rem", sm: "14px" },
          color: CARD_COLORS[tabKey].text,
          textAlign: "center",
        }}>
          {label}
        </Typography>

        {selectedProject && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
            <Tooltip title={`Download ${label} Excel`} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => handleCardExcelExport(e, tabKey, label)}
                  disabled={cardExcelLoading === tabKey}
                  sx={{
                    padding: "2px",
                    border: `1px solid ${CARD_COLORS[tabKey].border}`,
                    borderRadius: "4px",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.5)" },
                  }}
                >
                  {cardExcelLoading === tabKey ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <FileIcon sx={{ fontSize: "0.9rem", color: CARD_COLORS[tabKey].text }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Top Bar */}
      <Box sx={{
        display: "flex", gap: 2, mb: 2,
        mt: { xs: 3.5, sm: 0 },
        alignItems: "center",
      }}>
        <Autocomplete
          options={Array.isArray(projects) ? projects : []}
          getOptionLabel={(option) =>
            `${option.project_name || ""} | ${option.po_number || ""} | ${option.party_details?.company_name || option.party_name || ""}`
          }
          loading={loadingProjects}
          value={selectedProject}
          onChange={(e, v) => {
            setSelectedProject(v);
            setSelectedTab(undefined);
            setTabData([]);
          }}
          onInputChange={(e, value, reason) => {
            if (reason === "input") handleProjectSearch(value);
          }}
          ListboxProps={{
            onScroll: (event) => {
              const node = event.currentTarget;
              if (node.scrollTop + node.clientHeight === node.scrollHeight) {
                if (projectHasMore && !loadingProjects) {
                  setProjectPage((prev) => prev + 1);
                  fetchProjects(projectPage + 1, projectSearch);
                }
              }
            },
          }}
          noOptionsText="No projects found"
          sx={{ flex: 1 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Project"
              placeholder="Search project..."
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingProjects ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": { height: 36, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              }}
            />
          )}
        />

        <Tooltip title={excelLoading ? "Exporting all data..." : "Download All Data as Excel"} arrow>
          <span>
            <IconButton
              color="success"
              size="small"
              onClick={handleAllExcelExport}
              disabled={excelLoading || !selectedProject}
              sx={{
                borderRadius: "4px",
                padding: "6px",
                border: "1px solid #4caf50",
                "&:hover": { backgroundColor: "#e8f5e9" },
                "&:disabled": { border: "1px solid #ccc" },
              }}
            >
              {excelLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <FileIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Summary Cards */}
      {selectedProject && (
        <Box sx={{ mb: 2, minHeight: "200px" }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <SummaryCard
                tabKey="SO"
                label="Sales Order"
                onClick={() => { setSelectedTab("SO"); fetchReportData("SO"); }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <SummaryCard
                tabKey="PI"
                label="Proforma Invoice"
                onClick={() => { setSelectedTab("PI"); fetchReportData("PI"); }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <SummaryCard
                tabKey="PurchaseVoucher"
                label="Purchase List"
                onClick={() => { setSelectedTab("PurchaseVoucher"); fetchReportData("PurchaseVoucher"); }}
              />
            </Grid>
            {/* ✅ CHANGED: tabKey "Payment" → "PO" */}
            <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <SummaryCard
                tabKey="PO"
                label="Purchase Orders"
                onClick={() => { setSelectedTab("PO"); fetchReportData("PO"); }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <SummaryCard
                tabKey="IssueNoteInward"
                label="Issue Note Inward"
                onClick={() => { setSelectedTab("IssueNoteInward"); fetchReportData("IssueNoteInward"); }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <SummaryCard
                tabKey="IssueNoteOutward"
                label="Issue Note Outward"
                onClick={() => { setSelectedTab("IssueNoteOutward"); fetchReportData("IssueNoteOutward"); }}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {isMobile ? (
        <Box sx={{ mt: 2 }}>{renderMobileData()}</Box>
      ) : (
        <Box>{renderSection()}</Box>
      )}
    </Box>
  );
};

export default ProjectSummary;